import {Combobox} from '@headlessui/react'
import classNames from 'classnames'
import React, {Fragment, useEffect, useMemo, useRef, useState} from 'react'

import ChevronIcon from '../../shared/chevronIcon/ChevronIcon'
import Flag from '../icons/Flag'
import Icon, {Icons} from '../icons/Icon'
import {CountryCode, countryMap} from '../icons/types'
import Input from '../inputs/Input'

export interface CountrySelectProps {
  leadingIcon?: Icons;
  selectedValue: CountryCode;
  setSelectedValue: (option: CountryCode) => void;
  fullWidth?: boolean;
  blacklistCountries?: CountryCode[];
  preferredCountries?: CountryCode[];
  customCountryList?: CountryCode[];
  disabled?: boolean;
}

interface CountryOptionContentProps {
  active: boolean;
  selected: boolean;
  countryCode: string;
  name: string;
}

const CountryOptionContent = ({
  selected,
  countryCode,
  name,
  active
}: CountryOptionContentProps) => {
  const optionBgClassNames = classNames('', {
    'bg-kwota-primary-100': active
  })
  return (
    <div
      className={`select-none cursor-pointer h-9 px-4 flex gap-2 items-center font-semibold ${
        !selected && 'pl-10'
      }  ${optionBgClassNames}`}
    >
      {selected &&
        <Icon icon="tick" size="sm" className="fill-kwota-primary-500" />
      }
      <Flag countryCode={countryCode as CountryCode} className="border border-kwota-neutral-300" />
      <span className={'block font-normal truncate'}>{name}</span>
    </div>
  )
}

const getCountryMap = (list: CountryCode[]) =>
  Object.fromEntries(
    list.map(countryCode => [countryCode, countryMap[countryCode]])
  )

const CountrySelect = ({
  customCountryList,
  blacklistCountries,
  preferredCountries,
  selectedValue,
  setSelectedValue,
  disabled = false,
  fullWidth = false
}: CountrySelectProps) => {
  const [query, setQuery] = useState('')

  const wrapperClassNames = classNames('relative', {
    'w-full': fullWidth,
    'before:content-[""] before:absolute before:inset-0 before:bg-kwota-neutral-100/60 pointer-events-none':
      disabled
  })

  const dropdownClassNames =
    'absolute z-10 w-full transition-all duration-300 bg-kwota-neutral-100 text-left right-0 left-0 mx-auto max-h-60 font-titillium font-normal overflow-auto border-kwota-neutral-300 mt-2 py-1 border rounded-xl'

  const mapCountries = useMemo(
    () => customCountryList ? getCountryMap(customCountryList) : countryMap,
    []
  )

  const preferredCountryEntries = useMemo(
    () =>
      preferredCountries
        ? Object.entries(getCountryMap(preferredCountries))
        : undefined,
    []
  )

  const mapCountriesWithoutBlacklist = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(mapCountries).filter(
          ([countryCode, _name]) =>
            !blacklistCountries?.includes(countryCode as CountryCode)
        )
      ),
    []
  )

  const countryOptions = blacklistCountries
    ? mapCountriesWithoutBlacklist
    : mapCountries

  const countryOptionEntries = useMemo(
    () => Object.entries(countryOptions),
    []
  )

  const filteredCountryOptionEntries = useMemo(
    () =>
      query === ''
        ? countryOptionEntries
        : countryOptionEntries.filter(([countryCode, name]) => {
          return (
            countryCode.toLowerCase().includes(query.toLowerCase()) ||
              name.toLowerCase().includes(query.toLowerCase())
          )
        }),
    [query]
  )

  const inputRef = useRef<HTMLInputElement>(null)

  return (
    <Combobox
      as="div"
      value={selectedValue}
      onChange={e => {
        setQuery('')
        setSelectedValue(e)
      }}
      className={wrapperClassNames}
    >
      {({open}) => {
        useEffect(() => {
          if (!open) return
          inputRef.current?.focus()
        }, [open])

        return (
          <>
            {!open ?
              <Combobox.Button as={'div'}>
                <button
                  className="flex items-center w-full h-12 gap-2 px-3 text-base font-normal border rounded-xl border-kwota-neutral-300 font-titillium text-kwota-neutral-800 focus:outline-none focus-visible:border-kwota-primary"
                  type="button"
                  onClick={() => setQuery('')}
                >
                  <Flag countryCode={selectedValue} className="border border-kwota-neutral-300" />
                  {countryMap[selectedValue]}
                  <div className={'ml-auto pointer-events-none pr-2'}>
                    <ChevronIcon isOpen={open} />
                  </div>
                </button>
              </Combobox.Button>
              :
              <div className="flex items-center h-12 border rounded-xl border-kwota-neutral-300">
                <Combobox.Input
                  as={Input}
                  onChange={e => setQuery(e.target.value)}
                  displayValue={() => ''}
                  value={query}
                  ref={inputRef}
                />
              </div>
            }

            <div className="relative">
              <Combobox.Options
                as="ul"
                className={`${dropdownClassNames} ${
                  open ? 'opacity-100' : 'opacity-0'
                }`}
              >
                {preferredCountryEntries &&
                  <>
                    {preferredCountryEntries.map(([countryCode, name]) => {
                      return (
                        <Combobox.Option
                          key={countryCode}
                          as={Fragment}
                          value={countryCode}
                        >
                          {({active, selected}) =>
                            <li>
                              <CountryOptionContent
                                {...{active, selected, countryCode, name}}
                              />
                            </li>
                          }
                        </Combobox.Option>
                      )
                    })}
                    <p className="py-2 pl-2 text-sm font-semibold font-titillium text-kwota-neutral-500">
                      Others
                    </p>
                  </>
                }

                {filteredCountryOptionEntries
                  .filter(
                    ([countryCode]) =>
                      !preferredCountries?.includes(countryCode as CountryCode)
                  )
                  .map(([countryCode, name]) => {
                    return (
                      <Combobox.Option
                        key={countryCode}
                        as={Fragment}
                        value={countryCode}
                      >
                        {({active, selected}) =>
                          <li>
                            <CountryOptionContent
                              {...{active, selected, countryCode, name}}
                            />
                          </li>
                        }
                      </Combobox.Option>
                    )
                  })}
              </Combobox.Options>
            </div>
          </>
        )
      }}
    </Combobox>
  )
}

export default CountrySelect
